<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import RewardsAccordion from "@apps/rewards/components/RewardsAccordion/RewardsAccordion.svelte";
    import RewardsAccordionItem from "@apps/rewards/components/RewardsAccordion/RewardsAccordionItem.svelte";
    import RewardsGeneralTheme from "@apps/rewards/components/RewardsGeneralTheme.svelte";
    import type { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
    import type RewardsFaqQuery from "@apps/rewards/modules/Faq/query/RewardsFaqQuery";
    import type RewardsFaqService from "@apps/rewards/modules/Faq/service/RewardsFaqService";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import { normalizeLocale } from "@apps/rewards/utils/normalizeLocale/normalizeLocale";
    import type { LocalizationInfoProviderInterface } from "@lib/LocalizationInfoProvider/LocalizationInfoProviderInterface";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";
    import { marked } from "marked";
    import { lastValueFrom } from "rxjs";
    import { onMount } from "svelte";

    import Divider from "@components/Divider.svelte";
    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardsPageWidgetContainer from "../../components/RewardsPageWidgetContainer.svelte";
    import RewardsPageWidgetEmptyState from "../../components/RewardsPageWidgetEmptyState.svelte";
    import RewardsPageWidgetLayout from "../../components/RewardsPageWidgetLayout.svelte";
    import RewardsPageWidgetTitle from "../../components/RewardsPageWidgetTitle.svelte";
    import RewardsFaqPlusAnimatedIcon from "./components/icons/RewardsFaqPlusAnimatedIcon.svelte";
    import RewardsFaqPageWidgetSkeleton from "./components/skeleton/RewardsFaqPageWidgetSkeleton.svelte";

    //Props
    export let rewardsFaqService: RewardsFaqService;
    export let rewardsStorefrontValuesService: RewardsStorefrontValuesService;
    export let rewardsFaqQuery: RewardsFaqQuery;
    export let localizationInfoProvider: LocalizationInfoProviderInterface;
    export let sanitizer: HTMLSanitizerInterface;
    export let namespace: RewardsTranslationNamespaces;
    export let afterWidgetInject: () => void;

    const rewardsBranding$ =
        rewardsStorefrontValuesService.selectRewardsBranding();
    const requestStatus$ = rewardsFaqQuery.status$;
    const faqList$ = rewardsFaqQuery.faqList$;

    onMount(() => {
        lastValueFrom(
            rewardsFaqService.getRewardFaqs({
                locale: normalizeLocale(
                    localizationInfoProvider.getLanguageIsoCode()
                ),
            })
        );

        afterWidgetInject();
    });
</script>

{#if $rewardsBranding$}
    <RewardsGeneralTheme rewardsBrandingGeneral={$rewardsBranding$.general}>
        <div class="gw-rd-faq-page-widget">
            <RewardsPageWidgetContainer>
                <RewardsPageWidgetLayout>
                    {#if $requestStatus$ === STATUS.INITIAL_LOADING}
                        <RewardsFaqPageWidgetSkeleton />
                    {:else}
                        <RewardsPageWidgetTitle>
                            <T {namespace} translationKey="widget_title" />
                        </RewardsPageWidgetTitle>
                        <RewardsAccordion let:state>
                            {#each $faqList$ as { id, title, body }, index (id)}
                                <RewardsAccordionItem
                                    value={`item-${index}`}
                                    isItemMounted$={state.isItemMounted$}
                                    activeItem$={state.activeItem$}
                                >
                                    <div
                                        let:isOpen
                                        slot="button-content"
                                        class="gw-rd-faq-page-widget__accordion-button-content"
                                    >
                                        <Text
                                            component="span"
                                            variant="primary"
                                            size="lg"
                                            weight="semibold"
                                        >
                                            {title}
                                        </Text>
                                        <RewardsFaqPlusAnimatedIcon {isOpen} />
                                    </div>
                                    <div
                                        slot="accordion-content"
                                        class="gw-rd-faq-page-widget__accordion-content"
                                    >
                                        <Text
                                            component="p"
                                            variant="secondary"
                                            size="lg"
                                        >
                                            {@html sanitizer.sanitize(
                                                rewardsFaqService.extractString(
                                                    marked.parse(body)
                                                )
                                            )}
                                        </Text>
                                    </div>
                                </RewardsAccordionItem>
                                <Divider />
                            {:else}
                                <RewardsPageWidgetEmptyState>
                                    <Text slot="content">
                                        <T
                                            {namespace}
                                            translationKey="faq_empty_state"
                                        />
                                    </Text>
                                </RewardsPageWidgetEmptyState>
                            {/each}
                        </RewardsAccordion>
                    {/if}
                </RewardsPageWidgetLayout>
            </RewardsPageWidgetContainer>
        </div>
    </RewardsGeneralTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../../../themes/default/default.css";
    @import "../../../../../../themes/globals.css";

    .gw-rd-faq-page-widget {
        background-color: var(--gw-rd-white-color);

        &__accordion-button-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: var(--gw-rd-space-6);
            color: var(--gw-rd-primary-color);
        }

        &__accordion-content {
            padding: 0 var(--gw-rd-space-20) var(--gw-rd-space-6)
                var(--gw-rd-space-6);
            letter-spacing: 0.01rem;
            line-height: 20px;
            text-align: left;

            :global(p) {
                margin: 0;
            }
        }
    }
</style>
